class InfiniteGallery extends HTMLElement {
  constructor() {
    super();

    // Drag state
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.currentX = 0;
    this.currentY = 0;
    this.translateX = 0;
    this.translateY = 0;
    this.velocity = { x: 0, y: 0 };

    // Expansion state
    this.expandedTile = null;
    this.enableExpansion = this.dataset.enableExpansion === 'true';
    this.isExpanded = false; // Lock flag to prevent multiple expansions

    // Click detection
    this.dragDistance = 0;
    this.CLICK_THRESHOLD = 5; // pixels

    // Animation
    this.animationFrame = null;
    this.FRICTION = 0.92;
    this.VELOCITY_THRESHOLD = 0.1;

    // Grid dimensions (will be calculated)
    this.gridWidth = 0;
    this.gridHeight = 0;

    // Bind methods
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handleTileClick = this.handleTileClick.bind(this);
    this.animate = this.animate.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    console.log('InfiniteGallery: Connected');

    this.viewport = this.querySelector('.gallery-viewport');
    this.gridWrapper = this.querySelector('.gallery-grid-wrapper');
    this.originalMasonry = this.querySelector('.gallery-masonry');
    this.dragHint = this.querySelector('.gallery-drag-hint');
    this.overlay = this.querySelector('.gallery-overlay');

    console.log('Elements found:', {
      viewport: !!this.viewport,
      gridWrapper: !!this.gridWrapper,
      originalMasonry: !!this.originalMasonry,
      dragHint: !!this.dragHint,
      overlay: !!this.overlay
    });

    if (!this.viewport || !this.gridWrapper || !this.originalMasonry || !this.overlay) {
      console.error('InfiniteGallery: Required elements not found');
      return;
    }

    this.tiles = Array.from(this.originalMasonry.querySelectorAll('.gallery-tile'));

    console.log('Tiles found:', this.tiles.length);

    if (this.tiles.length === 0) {
      console.log('No tiles, skipping initialization');
      return; // Empty state, don't initialize
    }

    this.setupInfiniteGrid();
    this.setupEventListeners();
    this.setupDragHint();
    this.startAnimation();

    console.log('InfiniteGallery: Initialization complete');
  }

  disconnectedCallback() {
    this.removeEventListeners();
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  setupInfiniteGrid() {
    // Create 3x3 grid of duplicates for seamless infinite scrolling
    this.gridWrapper.innerHTML = '';

    // Store original for reference
    const originalHTML = this.originalMasonry.outerHTML;

    for (let y = 0; y < 3; y++) {
      for (let x = 0; x < 3; x++) {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = originalHTML;
        const clone = wrapper.firstElementChild;
        clone.classList.add('gallery-masonry-clone');
        clone.dataset.gridX = x;
        clone.dataset.gridY = y;
        this.gridWrapper.appendChild(clone);
      }
    }

    // Update references
    this.allGrids = Array.from(this.gridWrapper.querySelectorAll('.gallery-masonry'));

    console.log('Created', this.allGrids.length, 'grid duplicates');

    // Wait for all images to load before calculating dimensions
    const allImages = this.gridWrapper.querySelectorAll('img');
    let loadedCount = 0;
    const totalImages = allImages.length;

    const checkAllLoaded = () => {
      loadedCount++;
      if (loadedCount === totalImages || totalImages === 0) {
        setTimeout(() => {
          this.calculateGridDimensions();
          this.centerGrid();
          console.log('Grid setup complete');
        }, 100);
      }
    };

    if (totalImages === 0) {
      checkAllLoaded();
    } else {
      allImages.forEach(img => {
        if (img.complete) {
          checkAllLoaded();
        } else {
          img.addEventListener('load', checkAllLoaded);
          img.addEventListener('error', checkAllLoaded);
        }
      });
    }
  }

  setupEventListeners() {
    // Drag events
    this.viewport.addEventListener('pointerdown', this.handlePointerDown);
    this.viewport.addEventListener('pointermove', this.handlePointerMove);
    this.viewport.addEventListener('pointerup', this.handlePointerUp);
    this.viewport.addEventListener('pointercancel', this.handlePointerUp);
    this.viewport.addEventListener('pointerleave', this.handlePointerUp);

    // Prevent context menu on long press
    this.viewport.addEventListener('contextmenu', (e) => {
      if (this.isDragging) e.preventDefault();
    });

    // Click overlay to collapse expanded tile
    const handleOverlayClick = (e) => {
      if (this.expandedTile && this.dragDistance <= this.CLICK_THRESHOLD) {
        e.preventDefault();
        e.stopPropagation();
        this.collapseTile();
      }
    };

    this.overlay.addEventListener('click', handleOverlayClick);
    this.overlay.addEventListener('touchend', handleOverlayClick, { passive: false });

    // Tile click events (if expansion enabled)
    if (this.enableExpansion) {
      this.allGrids.forEach(grid => {
        const tiles = grid.querySelectorAll('.gallery-tile');
        tiles.forEach(tile => {
          tile.addEventListener('click', this.handleTileClick);
        });
      });
    }

    // Resize observer for responsive recalculation
    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.viewport);
  }

  removeEventListeners() {
    this.viewport.removeEventListener('pointerdown', this.handlePointerDown);
    this.viewport.removeEventListener('pointermove', this.handlePointerMove);
    this.viewport.removeEventListener('pointerup', this.handlePointerUp);
    this.viewport.removeEventListener('pointercancel', this.handlePointerUp);
    this.viewport.removeEventListener('pointerleave', this.handlePointerUp);

    if (this.enableExpansion && this.allGrids) {
      this.allGrids.forEach(grid => {
        const tiles = grid.querySelectorAll('.gallery-tile');
        tiles.forEach(tile => {
          tile.removeEventListener('click', this.handleTileClick);
        });
      });
    }
  }

  handlePointerDown(e) {
    // If clicking the overlay, let it handle collapse
    if (e.target === this.overlay) {
      return;
    }

    // If there's an expanded tile, check if we're clicking on it or a dimmed tile
    if (this.expandedTile) {
      const clickedTile = e.target.closest('.gallery-tile');
      // If clicking a dimmed tile or the expanded tile itself, collapse
      if (clickedTile && (clickedTile.classList.contains('dimmed') || clickedTile === this.expandedTile)) {
        this.collapseTile();
        e.preventDefault();
        return;
      }
      return;
    }

    this.isDragging = true;
    this.startX = e.clientX - this.translateX;
    this.startY = e.clientY - this.translateY;
    this.currentX = e.clientX;
    this.currentY = e.clientY;
    this.dragDistance = 0;
    this.velocity = { x: 0, y: 0 };

    this.viewport.style.cursor = 'grabbing';
    this.viewport.setPointerCapture(e.pointerId);
  }

  handlePointerMove(e) {
    if (!this.isDragging) return;

    const deltaX = e.clientX - this.currentX;
    const deltaY = e.clientY - this.currentY;

    this.currentX = e.clientX;
    this.currentY = e.clientY;

    this.translateX = e.clientX - this.startX;
    this.translateY = e.clientY - this.startY;

    // Track drag distance for click detection
    this.dragDistance += Math.abs(deltaX) + Math.abs(deltaY);

    // Capture velocity for momentum
    this.velocity.x = deltaX;
    this.velocity.y = deltaY;

    this.updateGridTransform();
  }

  handlePointerUp(e) {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.viewport.style.cursor = 'grab';

    if (e.pointerId !== undefined) {
      this.viewport.releasePointerCapture(e.pointerId);
    }
  }

  handleTileClick(e) {
    // Ignore if this was a drag
    if (this.dragDistance > this.CLICK_THRESHOLD) {
      return;
    }

    const tile = e.currentTarget;

    // If ANY tile is expanded (using lock flag)
    if (this.isExpanded) {
      // Stop everything - just collapse, never expand another
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();
      this.collapseTile();
      return;
    }

    // Nothing is expanded, so we can expand this tile
    e.stopPropagation();
    e.preventDefault();
    this.expandTile(tile);
  }

  expandTile(tile) {
    // Collapse previous if exists
    if (this.expandedTile) {
      this.collapseTile();
    }

    this.expandedTile = tile;
    this.isExpanded = true; // Set lock flag
    tile.classList.add('expanded');

    // Dim all other tiles
    this.allGrids.forEach(grid => {
      grid.querySelectorAll('.gallery-tile').forEach(t => {
        if (t !== tile) {
          t.classList.add('dimmed');
        }
      });
    });

    // Show overlay
    this.overlay.classList.add('visible');
  }

  collapseTile() {
    if (!this.expandedTile) return;

    this.expandedTile.classList.remove('expanded');
    this.isExpanded = false; // Clear lock flag

    // Remove dim from all tiles
    this.allGrids.forEach(grid => {
      grid.querySelectorAll('.gallery-tile').forEach(t => {
        t.classList.remove('dimmed');
      });
    });

    // Hide overlay
    this.overlay.classList.remove('visible');
    this.expandedTile = null;
  }

  updateGridTransform() {
    this.gridWrapper.style.transform = `translate(${this.translateX}px, ${this.translateY}px)`;
    this.checkBoundaries();
  }

  checkBoundaries() {
    if (!this.gridWidth || !this.gridHeight) return;

    // Seamless infinite scrolling - wrap position when crossing grid boundaries
    // Check horizontal wrapping
    if (this.translateX > 0) {
      // Scrolled too far right - wrap to left equivalent
      this.translateX -= this.gridWidth;
      this.startX += this.gridWidth;
    } else if (this.translateX < -this.gridWidth * 2) {
      // Scrolled too far left - wrap to right equivalent
      this.translateX += this.gridWidth;
      this.startX -= this.gridWidth;
    }

    // Check vertical wrapping
    if (this.translateY > 0) {
      // Scrolled too far down - wrap to up equivalent
      this.translateY -= this.gridHeight;
      this.startY += this.gridHeight;
    } else if (this.translateY < -this.gridHeight * 2) {
      // Scrolled too far up - wrap to down equivalent
      this.translateY += this.gridHeight;
      this.startY -= this.gridHeight;
    }
  }

  animate() {
    if (!this.isDragging) {
      // Apply momentum
      if (Math.abs(this.velocity.x) > this.VELOCITY_THRESHOLD || Math.abs(this.velocity.y) > this.VELOCITY_THRESHOLD) {
        this.translateX += this.velocity.x;
        this.translateY += this.velocity.y;

        this.velocity.x *= this.FRICTION;
        this.velocity.y *= this.FRICTION;

        this.updateGridTransform();
      } else {
        this.velocity = { x: 0, y: 0 };
      }
    }

    this.animationFrame = requestAnimationFrame(this.animate);
  }

  startAnimation() {
    if (!this.animationFrame) {
      this.animate();
    }
  }

  calculateGridDimensions() {
    if (this.allGrids.length === 0) return;

    // Use the first grid to calculate dimensions
    const firstGrid = this.allGrids[0];
    const viewportRect = this.viewport.getBoundingClientRect();

    // Calculate width (should be viewport width)
    this.gridWidth = firstGrid.offsetWidth;

    // Calculate height - actual height of the masonry container
    this.gridHeight = firstGrid.offsetHeight;

    this.viewportWidth = viewportRect.width;
    this.viewportHeight = viewportRect.height;

    console.log('Grid dimensions:', {
      gridWidth: this.gridWidth,
      gridHeight: this.gridHeight,
      viewportWidth: this.viewportWidth,
      viewportHeight: this.viewportHeight
    });
  }

  centerGrid() {
    // Position the wrapper so the center grid (1,1 in 3x3) is visible
    // This is the 5th grid (index 4)
    const centerOffset = {
      x: -this.gridWidth,
      y: -this.gridHeight
    };

    this.translateX = centerOffset.x;
    this.translateY = centerOffset.y;
    this.startX = -centerOffset.x;
    this.startY = -centerOffset.y;

    console.log('Centering grid at:', centerOffset);

    this.updateGridTransform();
  }

  handleResize() {
    this.calculateGridDimensions();
    this.centerGrid();
  }

  setupDragHint() {
    if (!this.dragHint) return;

    let canHide = false;
    let hasInteracted = false;

    // Allow hiding after 4 seconds minimum (wait for loading screen)
    setTimeout(() => {
      canHide = true;
      // If user already interacted, hide now
      if (hasInteracted) {
        hideHint();
      }
    }, 4000);

    // Hide hint
    const hideHint = () => {
      if (this.dragHint && canHide) {
        this.dragHint.classList.add('hidden');
        // Remove hint completely after animation
        setTimeout(() => {
          if (this.dragHint && this.dragHint.parentNode) {
            this.dragHint.remove();
          }
        }, 500);
      }
    };

    // Track first interaction
    const handleInteraction = () => {
      hasInteracted = true;
      if (canHide) {
        hideHint();
      }
    };

    // Hide on first drag
    this.viewport.addEventListener('pointerdown', handleInteraction, { once: true });

    // Also hide on first touch (for mobile)
    this.viewport.addEventListener('touchstart', handleInteraction, { once: true, passive: true });

    // Auto-hide after 6 seconds regardless
    setTimeout(() => {
      canHide = true;
      hideHint();
    }, 6000);
  }
}

// Register the custom element
if (!customElements.get('infinite-gallery')) {
  customElements.define('infinite-gallery', InfiniteGallery);
}
